import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavIcons as Icons } from "../ui/Icons";

// =============================================================================
// CountryFilterFlyout — hover-reveal multi-select country picker for the
// Transaction Log "Country" column header.
// =============================================================================
// WHY a flyout, not sort + click-to-filter: countries aren't useful sorted by
// name; the user needs to FIND and pick specific ones. So the header opens a
// searchable checkbox picker instead (Mako, Sep 4 2026).
//
// SAFE-TRIANGLE (/triangle): this is the single-trigger "defer the CLOSE"
// variant, copied from Sidebar.tsx OrgFlyout (commit 977fccca) per the
// codebase convention of keeping separate copies rather than a shared hook.
// The one adaptation: OrgFlyout opens to the RIGHT (near edge vertical, uses
// slope=dy/dx to top/bottom corners); this panel drops DOWN (near edge
// horizontal), so the aim test uses INVERSE slope (dx/dy) against the panel's
// top-left/top-right corners. Same "cone narrows as the cursor dives in" logic.
//
// GLASS: panel is `detail-overlay` (NOT liquid-glass), Portal'd to body, fades
// with opacity only (no transform — backdrop-filter blur trap). See /triangle.
// =============================================================================

const FLYOUT_CLOSE_DELAY = 220; // ms — grace before the panel closes on leave
const AIM_RECHECK = 200; // ms — re-check cadence while the cursor aims at the panel
const MOUSE_TRACK = 3; // recent cursor samples kept for the aim calculation

// Inverse slope (dx/dy) — the downward-panel analog of OrgFlyout's slope(dy/dx).
const islope = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  (b.x - a.x) / (b.y - a.y || 0.0001);

export interface CountryOption {
  country: string;
  count: number;
}

const CountryFilterFlyout: React.FC<{
  options: CountryOption[];
  selected: Set<string> | null; // null => no filter (all shown)
  onChange: (values: string[]) => void;
}> = ({ options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | null>(null);
  const aimTimer = useRef<number | null>(null);
  const pinnedRef = useRef(false);
  const mouseLocs = useRef<{ x: number; y: number }[]>([]);
  const [pos, setPos] = useState({ top: 0, left: 0, opacity: 0 });

  // Local selection mirrors the active filter; re-seeded from `selected` each
  // time the panel opens so an external "clear all" is reflected. null => all.
  const allCountries = useMemo(() => options.map((o) => o.country), [options]);
  const [sel, setSel] = useState<Set<string>>(() => new Set(allCountries));

  const isFiltered = selected !== null && selected.size > 0 && selected.size < allCountries.length;
  const activeCount = isFiltered ? (selected as Set<string>).size : 0;

  const clearHide = () => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const clearAim = () => {
    if (aimTimer.current) {
      window.clearTimeout(aimTimer.current);
      aimTimer.current = null;
    }
  };

  // Menu-aim: is the cursor DIVING toward the open panel below the trigger?
  const aimingAtPanel = () => {
    const panel = panelRef.current;
    if (!open || !panel) return false;
    const locs = mouseLocs.current;
    const loc = locs[locs.length - 1];
    const prev = locs[locs.length - 2] || loc;
    if (!loc || loc === prev) return false;
    const r = panel.getBoundingClientRect();
    const BUF = 40;
    // Panel is BELOW → near edge is the TOP edge; corners padded left/right.
    const left = { x: r.left - BUF, y: r.top };
    const right = { x: r.right + BUF, y: r.top };
    // Cone narrows (mirrors OrgFlyout's top-decreases / bottom-increases).
    return islope(loc, left) < islope(prev, left) && islope(loc, right) > islope(prev, right);
  };

  const maybeHide = () => {
    if (pinnedRef.current) return;
    if (aimingAtPanel()) {
      const settledAt = mouseLocs.current[mouseLocs.current.length - 1];
      aimTimer.current = window.setTimeout(() => {
        const now = mouseLocs.current[mouseLocs.current.length - 1];
        if (now === settledAt) {
          clearAim();
          setOpen(false);
        } else {
          maybeHide(); // still diving toward the panel → keep it alive
        }
      }, AIM_RECHECK);
    } else {
      setOpen(false);
    }
  };
  const scheduleHide = () => {
    clearHide();
    clearAim();
    hideTimer.current = window.setTimeout(maybeHide, FLYOUT_CLOSE_DELAY);
  };

  const openNow = () => {
    clearHide();
    clearAim();
    // Re-seed local selection from the live filter each open.
    setSel(selected ? new Set(selected) : new Set(allCountries));
    setQuery("");
    setOpen(true);
  };
  const togglePin = () => {
    clearHide();
    clearAim();
    const next = !pinned;
    setPinned(next);
    pinnedRef.current = next;
    if (next) {
      setSel(selected ? new Set(selected) : new Set(allCountries));
      setQuery("");
    }
    setOpen(next);
  };

  useEffect(
    () => () => {
      clearHide();
      clearAim();
    },
    []
  );

  // Track the cursor globally while open (panel is Portal'd off the table).
  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      const locs = mouseLocs.current;
      locs.push({ x: e.clientX, y: e.clientY });
      if (locs.length > MOUSE_TRACK) locs.shift();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (hideTimer.current) {
          window.clearTimeout(hideTimer.current);
          hideTimer.current = null;
        }
        if (aimTimer.current) {
          window.clearTimeout(aimTimer.current);
          aimTimer.current = null;
        }
        setPinned(false);
        pinnedRef.current = false;
        setOpen(false);
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Position the panel below the trigger (fixed coords — Portal is on body).
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const r = trigger.getBoundingClientRect();
      const GAP = 6;
      const w = panel.offsetWidth;
      let left = r.left;
      const maxLeft = window.innerWidth - w - 12;
      if (left > maxLeft) left = maxLeft;
      if (left < 12) left = 12;
      let top = r.bottom + GAP;
      const maxTop = window.innerHeight - panel.offsetHeight - 12;
      if (top > maxTop) top = Math.max(12, maxTop);
      setPos({ top: Math.round(top), left: Math.round(left), opacity: 1 });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, query]);

  const commit = (nextSel: Set<string>) => {
    setSel(nextSel);
    onChange(Array.from(nextSel));
  };
  const toggle = (country: string) => {
    const next = new Set(sel);
    if (next.has(country)) next.delete(country);
    else next.add(country);
    commit(next);
  };
  const isolate = (country: string) => commit(new Set([country]));
  const selectAll = () => commit(new Set(allCountries));

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.country.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid="country-filter-trigger"
        onMouseEnter={openNow}
        onMouseLeave={scheduleHide}
        onFocus={openNow}
        onClick={togglePin}
        aria-expanded={open}
        aria-haspopup="menu"
        title="Hover to filter by country"
        className={`inline-flex items-center gap-1 token-11 rounded-md px-1.5 py-1 select-none cursor-pointer transition-colors ${
          open || isFiltered
            ? "bg-black/[0.05] text-glass"
            : "text-glass-secondary hover:bg-black/[0.04]"
        }`}
        style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}
      >
        <span>Country</span>
        {activeCount > 0 && (
          <span
            className="token-9 px-1 py-px rounded bg-ds-accent/15 text-ds-accent tabular-nums leading-none"
            style={{ letterSpacing: 0 }}
          >
            {activeCount}
          </span>
        )}
        <span className="text-glass-muted opacity-60 text-[8px] leading-none">▼</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label="Filter by country"
            data-testid="country-filter-panel"
            onMouseEnter={() => {
              clearHide();
              clearAim();
            }}
            onMouseLeave={scheduleHide}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              opacity: pos.opacity,
              zIndex: 9990,
            }}
            className="detail-overlay border border-black/[0.08] rounded-xl shadow-xl w-56 p-1.5 transition-opacity duration-150"
          >
            <div className="flex items-center gap-1.5 px-2 h-7 rounded-lg bg-black/[0.03] mb-1">
              <Icons.Search className="w-3 h-3 text-glass-muted flex-shrink-0" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                data-testid="country-filter-search"
                className="flex-1 min-w-0 bg-transparent token-calendar text-glass placeholder:text-glass-muted outline-none"
              />
            </div>

            <div className="flex items-center justify-between px-2 pb-1 mb-0.5 border-b border-black/[0.06]">
              <button
                type="button"
                data-testid="country-filter-all"
                onClick={selectAll}
                className="token-9 uppercase tracking-wide text-ds-accent hover:opacity-80 font-semibold"
              >
                Select all
              </button>
              <span className="token-9 text-glass-muted tabular-nums" style={{ letterSpacing: 0 }}>
                {isFiltered ? `${sel.size} / ${allCountries.length}` : `${allCountries.length}`}
              </span>
            </div>

            <div className="max-h-60 overflow-y-auto -mr-1 pr-1" data-testid="country-filter-list">
              {filteredOptions.map((o) => {
                const checked = sel.has(o.country);
                return (
                  <div
                    key={o.country}
                    role="menuitemcheckbox"
                    aria-checked={checked}
                    data-testid="country-option"
                    data-country={o.country}
                    className="flex items-center gap-2 pl-1.5 pr-2 h-7 rounded-md hover:bg-black/[0.035] group"
                  >
                    <button
                      type="button"
                      onClick={() => toggle(o.country)}
                      aria-label={`Toggle ${o.country}`}
                      className={`flex-shrink-0 w-[15px] h-[15px] rounded-[4px] border flex items-center justify-center transition-colors ${
                        checked
                          ? "bg-ds-accent border-ds-accent"
                          : "border-black/20 group-hover:border-black/35"
                      }`}
                    >
                      {checked && <Icons.Check className="w-2.5 h-2.5 text-white" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => isolate(o.country)}
                      title="Show only this country"
                      className="flex-1 flex items-center justify-between gap-2 text-left min-w-0"
                    >
                      <span className="truncate token-calendar text-glass-secondary group-hover:text-glass">
                        {o.country}
                      </span>
                      <span
                        className="flex-shrink-0 token-9 text-glass-muted tabular-nums"
                        style={{ letterSpacing: 0 }}
                      >
                        {o.count.toLocaleString()}
                      </span>
                    </button>
                  </div>
                );
              })}
              {filteredOptions.length === 0 && (
                <div className="px-2 py-2 token-calendar text-glass-muted">No match</div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default CountryFilterFlyout;
